/**
 * InvoicePreviewDialog — "View" the generated invoice, then Print/Save-PDF or Send.
 * The invoice document is self-contained (inline <style>), so Print simply writes
 * the rendered element into a new window.
 */
import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, Send, Loader2 } from "lucide-react";
import { InvoiceDocument } from "./InvoiceDocument";
import type { InvoiceData } from "@/lib/invoice";
import { isOffsetInvoice } from "@/types/billing";

export function InvoicePreviewDialog({
  open,
  onOpenChange,
  data,
  onSend,
  sending,
  sentAt,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  data: InvoiceData | null;
  onSend?: () => void;
  sending?: boolean;
  sentAt?: string | null;
}) {
  const docRef = useRef<HTMLDivElement>(null);
  const [printing, setPrinting] = useState(false);
  // An offset invoice asks for no payment, so it needs no payable account —
  // gating Send on `data.bank` alone (which is always undefined for one, see
  // buildManagementInvoiceData) would permanently disable Send for every
  // offset invoice that lands in this dialog. Mirrors canSendInvoice in
  // useManagementInvoiceActions.tsx, which gates the same action on the
  // stamped-template dialog.
  const canSend =
    isOffsetInvoice({ settlement: data?.settlement }) || !!data?.bank;

  const handlePrint = () => {
    const el = docRef.current?.querySelector(".cl-invoice");
    if (!el) return;
    setPrinting(true);
    const w = window.open("", "_blank", "width=820,height=1000");
    if (!w) {
      setPrinting(false);
      return;
    }
    w.document.write(
      `<!doctype html><html><head><title>${data?.invoiceNo ?? "Invoice"}</title></head><body>${el.outerHTML}</body></html>`
    );
    w.document.close();
    w.focus();
    // Give the logo image a moment to load before printing.
    setTimeout(() => {
      w.print();
      setPrinting(false);
    }, 350);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>Invoice {data?.invoiceNo}</DialogTitle>
        </DialogHeader>

        <div ref={docRef} className="px-6">
          <div className="rounded-lg border bg-white shadow-sm overflow-hidden">
            {data ? <InvoiceDocument data={data} /> : null}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 gap-2 sm:gap-2 flex-row justify-end items-center">
          {sentAt ? (
            <span className="mr-auto text-xs text-muted-foreground">
              Sent {new Date(sentAt).toLocaleString("en-GB")}
            </span>
          ) : null}
          <Button variant="outline" onClick={handlePrint} disabled={printing}>
            <Printer className="h-4 w-4 mr-2" />
            {printing ? "Preparing…" : "Print / Save PDF"}
          </Button>
          {onSend ? (
            <Button
              onClick={onSend}
              disabled={sending || !canSend}
              title={
                canSend
                  ? undefined
                  : "No coliviq bank account resolves for this property — set a platform bank account (or the property's) before sending"
              }
            >
              {sending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send invoice
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
